import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { DataContext } from '@services/data-context';
import { MessageService } from '@services';
import { ProjectContextService } from '@data-services/projects';
import type { ProjectContext } from '@models/project-context.model';
import type { ProjectUserRole } from '@models/projects';

/** Route data key listing the project roles allowed on a route. Routes without it are open to every role except Customer. */
export const ALLOWED_ROLES_ROUTE_DATA_KEY = 'allowedRoles';

const DEFAULT_ALLOWED_ROLES: readonly ProjectUserRole[] = ['Admin', 'ServiceLeader', 'ServiceWorker'];

export const projectAuthGuard: CanActivateFn = async (route) => {
  const dataContext = inject(DataContext);
  const messageService = inject(MessageService);
  const projectContextService = inject(ProjectContextService);
  const router = inject(Router);

  const projectId = route.paramMap.get('projectId') ?? dataContext.projectContext()?.id;

  if (!projectId) {
    await rejectUserFromProject(router, messageService);
    return false;
  }

  const existingContext = dataContext.projectContext();
  if (existingContext?.id === projectId && existingContext.currentUser) {
    refreshProjectContextInBackground(route, projectId, projectContextService, dataContext, router, messageService);
    return resolveRoleAccess(route, existingContext, router);
  }

  try {
    const projectContext: ProjectContext = await projectContextService.getProjectContextAsync(projectId);

    if (!projectContext.currentUser) {
      await rejectUserFromProject(router, messageService);
      return false;
    }

    dataContext.setProjectContext(projectContext);
    return resolveRoleAccess(route, projectContext, router);
  } catch (error) {
    console.error('Failed to load project context:', error);
    await rejectUserFromProject(router, messageService);
    return false;
  }
};

function resolveRoleAccess(route: ActivatedRouteSnapshot, projectContext: ProjectContext, router: Router): true | UrlTree {
  const userRole: ProjectUserRole = projectContext.currentUser.userRole;
  const allowedRoles: readonly ProjectUserRole[] = route.data[ALLOWED_ROLES_ROUTE_DATA_KEY] ?? DEFAULT_ALLOWED_ROLES;
  if (allowedRoles.includes(userRole)) return true;

  const landingPath = userRole === 'Customer' ? 'customer-doors' : 'services';
  return router.createUrlTree(['/projects', projectContext.id, landingPath]);
}

async function refreshProjectContextInBackground(
  route: ActivatedRouteSnapshot,
  projectId: string,
  projectContextService: ProjectContextService,
  dataContext: DataContext,
  router: Router,
  messageService: MessageService
): Promise<void> {
  try {
    const refreshedContext: ProjectContext = await projectContextService.getProjectContextAsync(projectId);
    if (hasLeftProject(router, projectId)) return;

    dataContext.setProjectContext(refreshedContext);

    if (!refreshedContext.currentUser) {
      await rejectUserFromProject(router, messageService);
      return;
    }

    const roleAccess: true | UrlTree = resolveRoleAccess(route, refreshedContext, router);
    if (roleAccess !== true) await router.navigateByUrl(roleAccess, { replaceUrl: true });
  } catch (error) {
    console.error('Failed to refresh project context:', error);
    if (hasLeftProject(router, projectId)) return;

    await rejectUserFromProject(router, messageService);
  }
}

/** A background refresh is stale once the user has navigated away from the project it was started for. */
function hasLeftProject(router: Router, projectId: string): boolean {
  return !router.url.startsWith(`/projects/${projectId}/`);
}

async function rejectUserFromProject(
  router: Router,
  messageService: MessageService
): Promise<void> {
  messageService.showErrorMessage('auth.errors.projectAccessDenied');
  await router.navigate(['/projects']);
}
